import React, { useMemo, useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Container,
  Paper,
  Box,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Alert,
} from "@mui/material";
import { CheckCircle, Cancel } from "@mui/icons-material";
import { setForgotPassword } from "../services/api";
import logo from "../assets/logos/logo.png";
import loginIcon from "../assets/icons/loginIcon.png";
import "./Login.css";

const criteriaList = [
  { key: "digit", label: "Sadece rakam içermeli", test: /^\d+$/ },
  { key: "length", label: "En az 6 karakter", test: /^\d{6,}$/ },
];

const ResetPassword = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const phone = location.state?.phone;

  useEffect(() => {
    if (!phone) navigate("/forgot-password");
  }, [phone, navigate]);

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");

  const passwordChecks = useMemo(() => {
    const map = {};
    criteriaList.forEach(({ key, test }) => {
      map[key] = test.test(password);
    });
    return map;
  }, [password]);

  const isPasswordStrong = Object.values(passwordChecks).every(Boolean);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (!isPasswordStrong) {
      setError("Şifre sadece rakam içermeli ve en az 6 karakter olmalıdır.");
      return;
    }

    if (!password || password !== confirm) {
      setError("Şifreler aynı olmalıdır.");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        TELEFON: phone,
        YENI_SIFRE: password,
        YENI_SIFRE_ONAY: confirm,
      };

      console.log("Gönderilen payload:", payload);

      const response = await setForgotPassword(payload);

      console.log("Backend response:", response);

      setSuccess("Şifreniz güncellendi. Giriş ekranına yönlendiriliyorsunuz…");
      setTimeout(() => navigate("/login"), 2000);
    } catch (err) {
      console.error("Tam hata:", err);
      console.error("Response data:", err.response?.data);
      console.error("Response status:", err.response?.status);

      setError(
        err.response?.data?.Error ||
          err.response?.data?.message ||
          err.response?.data?.Message ||
          "Şifre güncellenirken bir hata oluştu. Lütfen tekrar deneyin."
      );
    } finally {
      setLoading(false);
    }
  };

  const renderCriterion = ({ key, label }) => {
    const passed = passwordChecks[key];
    const Icon = passed ? CheckCircle : Cancel;
    return (
      <Box
        key={key}
        sx={{
          display: "flex",
          alignItems: "center",
          color: passed ? "success.main" : "text.secondary",
          mb: 0.5,
        }}
      >
        <Icon
          sx={{ fontSize: 18, mr: 1 }}
          color={passed ? "success" : "disabled"}
        />
        <Typography variant="body2">{label}</Typography>
      </Box>
    );
  };

  return (
    <div className="login-page-container">
      <div className="left-panel">
        <Container
          component={Paper}
          elevation={6}
          className="login-card"
          maxWidth={false}
        >
          <Box className="login-header">
            <img src={logo} alt="eKumes Logo" className="login-logo" />
          </Box>

          <Typography variant="h4" className="login-title" gutterBottom>
            Yeni Şifre Belirle
          </Typography>
          <Typography variant="body2" sx={{ mb: 3, color: "#666" }}>
            {phone} numarası için yeni şifrenizi girin.
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {success}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit}>
            <TextField
              label="Yeni Şifre (Sadece Rakam)"
              type="password"
              fullWidth
              value={password}
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, "");
                setPassword(value);
              }}
              inputProps={{
                inputMode: "numeric",
                pattern: "[0-9]*",
              }}
              sx={{ mb: 2 }}
            />

            <TextField
              label="Yeni Şifre (Tekrar)"
              type="password"
              fullWidth
              value={confirm}
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, "");
                setConfirm(value);
              }}
              inputProps={{
                inputMode: "numeric",
                pattern: "[0-9]*",
              }}
              sx={{ mb: 2 }}
            />

            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, color: "#333" }}>
                Şifre Kriterleri:
              </Typography>
              {criteriaList.map(renderCriterion)}
            </Box>

            <Button
              variant="contained"
              type="submit"
              fullWidth
              disabled={loading}
              className="login-button"
            >
              {loading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                "Şifreyi Güncelle"
              )}
            </Button>
          </Box>
        </Container>
      </div>

      <div className="right-panel">
        <div className="image-frame">
          <img
            src={loginIcon}
            alt="Reset Visual"
            className="side-image"
            draggable="false"
          />
          <div className="welcome-overlay">
            <Typography variant="h3" className="welcome-text">
              Güçlü Şifre Oluşturun
            </Typography>
            <Typography variant="subtitle1" className="welcome-subtext">
              Sadece rakam girişi yapabilirsiniz
            </Typography>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
